import { shortId } from "@@/utilities/generate-id";
import type {
    MinMaxRange} from "@@/utilities/set-within-range";
import {
    minMaxRangeZodSchema,
} from "@@/utilities/set-within-range";
import type { Except } from "type-fest";
import { z } from "zod";
import { isoAndUnixZodSchema, nowIsoAndUnix } from "../date-time/iso-and-unix";
import {
    metaContentFactory,
    metaContentZodSchema,
} from "../meta-content/meta-content";
import type { Price} from "../prices/price";
import { priceZodSchema, zeroPrice } from "../prices/price";
import { amountZodSchema } from "../units-and-quantities/amount";
import type {
    AvailableQuantity} from "../units-and-quantities/quantity";
import {
    availableQuantityZodSchema,
} from "../units-and-quantities/quantity";
import type {
    OptionalId} from "./optional-id";
import {
    optionalIdFactory,
    optionalIdZodSchema,
} from "./optional-id";

const optionalZodSchema = z.object({
    _id: optionalIdZodSchema,
    _type: z.literal("OPTIONAL"),
    _createdAt: isoAndUnixZodSchema,
    optionalGroupId: z.string(),
    slug: z.string(),
    meta: metaContentZodSchema,
    price: priceZodSchema,
    minMax: minMaxRangeZodSchema.optional(),
    amount: amountZodSchema.optional(),
    availableQuantity: availableQuantityZodSchema,
});
type Optional = z.infer<typeof optionalZodSchema>;

type FactoryParams = Except<
    Optional,
    | "_id"
    | "_createdAt"
    | "_type"
    | "slug"
    | "meta"
    | "amount"
    | "availableQuantity"
>;
const optionalFactory = (
    params: FactoryParams & {
        readonly _id?: OptionalId;
        readonly title: string;
        readonly price?: Price;
        readonly minMax?: MinMaxRange;
        readonly availableQuantity?: AvailableQuantity;
    },
): Optional => {
    const { _id, title, ...rest } = params;
    const optional: Optional = {
        _id: _id || optionalIdFactory(),
        _type: "OPTIONAL",
        _createdAt: nowIsoAndUnix(),
        slug: shortId(4),
        meta: metaContentFactory({ title }),
        ...rest,
        availableQuantity: params.availableQuantity ?? "INFINITE",
        price: params.price || zeroPrice,
    };

    return optional;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isOptional = (item: any): item is Optional => {
    return item?._type === "OPTIONAL";
};

export { isOptional, optionalFactory, optionalZodSchema };
export type { Optional };
